import { Button, DatePicker, Space } from 'antd';
import dayjs from 'dayjs';
import { useCallback, memo, useMemo } from 'react';
import { getQuickFilterDates } from './utils/filterHelpers';

const { RangePicker } = DatePicker;

const QUICK_LABELS = {
    today: 'Today',
    yesterday: 'Yesterday',
    last7days: 'Last 7 Days',
    last30days: 'Last 30 Days',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
};

const DateRangeFilter = memo((({
    value,
    onChange,
    error,
    quickFilters = [],
    style,
    placeholder,
    format = 'YYYY-MM-DD',
    showTime = false,
    disabled = false,
    allowClear = true,
}) => {
    // Convert value to dayjs objects only when the actual date values change
    // Use timestamps for comparison to avoid unnecessary re-renders
    const rangeValue = useMemo(() => {
        if (value?.start && value?.end) {
            return [dayjs(value.start), dayjs(value.end)];
        }
        return null;
    }, [value?.start?.getTime?.(), value?.end?.getTime?.()]);

    // Quick filter handler - simple and direct
    const handleQuickFilter = useCallback(
        (quickType) => {
            const dates = getQuickFilterDates(quickType);
            if (!dates) return;

            onChange({
                start: dayjs(dates.start).startOf('day').toDate(),
                end: dayjs(dates.end).endOf('day').toDate(),
            });
        },
        [onChange]
    );

    // RangePicker change handler
    const handleDateChange = useCallback(
        (dates) => {
            if (!dates) {
                onChange(null);
                return;
            }

            onChange({
                start: dayjs(dates[0]).startOf('day').toDate(),
                end: dayjs(dates[1]).endOf('day').toDate(),
            });
        },
        [onChange]
    );

    return (
        <div>
            <RangePicker
                value={rangeValue}
                onChange={handleDateChange}
                style={style}
                placeholder={placeholder}
                format={format}
                showTime={showTime}
                disabled={disabled}
                allowClear={allowClear}
                status={error ? 'error' : ''}
                dropdownClassName="table-filter-datepicker"
                popupStyle={{ zIndex: 99999 }}
                getPopupContainer={() => document.body}
            />

            {quickFilters.length > 0 && (
                <Space style={{ marginTop: 8 }} wrap>
                    {quickFilters.map((quick) => (
                        <Button
                            key={quick}
                            size="small"
                            onClick={() => handleQuickFilter(quick)}
                            disabled={disabled}
                        >
                            {QUICK_LABELS[quick] || quick}
                        </Button>
                    ))}
                </Space>
            )}

            {error && (
                <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: 4 }}>
                    {error}
                </div>
            )}
        </div>
    );
}), (prevProps, nextProps) => {
    // Custom comparison function for memo
    // Only re-render if these specific props change
    const isSameValue =
        prevProps.value?.start?.getTime?.() === nextProps.value?.start?.getTime?.() &&
        prevProps.value?.end?.getTime?.() === nextProps.value?.end?.getTime?.();

    return (
        isSameValue &&
        prevProps.error === nextProps.error &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.format === nextProps.format &&
        prevProps.showTime === nextProps.showTime &&
        prevProps.allowClear === nextProps.allowClear &&
        prevProps.onChange === nextProps.onChange
    );
});

DateRangeFilter.displayName = 'DateRangeFilter';

DateRangeFilter.defaultProps = {
    style: { width: '100%' },
    placeholder: ['Start Date', 'End Date'],
};

export default DateRangeFilter;
